import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { fmtIST } from '../lib/datetime.js';

export default function Inbox() {
  const [rows, setRows]   = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [offset, setOffset] = useState(0);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [busy, setBusy] = useState(true);
  const limit = 50;

  async function load(off = 0, unreadOnly = onlyUnread) {
    setBusy(true);
    try {
      const r = await api.get(`/api/whatsapp/inbound?limit=${limit}&offset=${off}${unreadOnly ? '&only_unread=1' : ''}`);
      setRows(r.rows || []);
      setTotal(r.total || 0);
      setUnread(r.unread || 0);
      setOffset(off);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(0); /* eslint-disable-line */ }, []);
  useEffect(() => { const t = setInterval(() => load(offset, onlyUnread), 15000); return () => clearInterval(t); /* eslint-disable-line */ }, [offset, onlyUnread]);

  async function markRead(id) {
    try { await api.post(`/api/whatsapp/inbound/${id}/read`); load(offset, onlyUnread); }
    catch {}
  }
  async function markAllRead() {
    try { await api.post('/api/whatsapp/inbound/mark-all-read'); load(offset, onlyUnread); }
    catch {}
  }

  return (
    <div className="px-8 py-6 max-w-[1100px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Inbox</h1>
          <p className="text-sm text-slate-500 mt-0.5">Incoming WhatsApp messages to your business number — replies + cold pings, known contacts + strangers.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={onlyUnread} onChange={e => { setOnlyUnread(e.target.checked); load(0, e.target.checked); }} />
            Unread only
          </label>
          {unread > 0 && <button className="btn-ghost btn-sm" onClick={markAllRead}>Mark all read ({unread})</button>}
          <button className="btn-ghost btn-sm" onClick={() => load(offset, onlyUnread)} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{total.toLocaleString()} message{total === 1 ? '' : 's'}{onlyUnread ? ' (unread)' : ''}</span>
          {unread > 0 && <span className="text-amber-700">{unread} unread overall</span>}
        </div>
        {!rows.length ? (
          <div className="px-5 py-16 text-center text-slate-400 text-sm">{busy ? 'Loading…' : onlyUnread ? 'No unread messages.' : 'No incoming messages yet.'}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map(r => <Row key={r.id} r={r} onMarkRead={() => markRead(r.id)} />)}
          </ul>
        )}
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>{rows.length ? `${offset + 1}–${Math.min(offset + limit, total)} of ${total.toLocaleString()}` : ''}</span>
          <div className="flex gap-2">
            <button className="btn-ghost btn-sm" disabled={offset === 0 || busy} onClick={() => load(Math.max(0, offset - limit), onlyUnread)}>← Prev</button>
            <button className="btn-ghost btn-sm" disabled={offset + limit >= total || busy} onClick={() => load(offset + limit, onlyUnread)}>Next →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ r, onMarkRead }) {
  const isUnread = !r.read_at;
  return (
    <li className={`px-5 py-3 flex items-start gap-4 hover:bg-slate-50/60 ${isUnread ? 'bg-amber-50/30' : ''}`}>
      <div className="shrink-0 w-3 mt-1.5">
        {isUnread && <span className="block w-2 h-2 rounded-full bg-amber-500" title="Unread" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 truncate">
            {r.contact_name || r.from_name || 'Unknown sender'}
          </span>
          <span className="text-xs text-slate-500 font-mono">+{r.from_phone}</span>
          {!r.contact_id && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">stranger</span>}
          {r.campaign_name && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 truncate max-w-[180px]" title={r.campaign_name}>{r.campaign_name}</span>}
          {r.message_type !== 'text' && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">{r.message_type}</span>}
        </div>
        <div className="text-sm text-slate-700 mt-1 break-words">
          {r.text || <span className="text-slate-400">[no text · {r.message_type}]</span>}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">{fmtIST(r.received_at)}</div>
        {isUnread && <button className="text-[11px] text-slate-500 hover:text-slate-800 mt-1" onClick={onMarkRead}>Mark read</button>}
      </div>
    </li>
  );
}
